/**
 * Provider selector display hydration — stale-while-revalidate labels.
 * RAW provider_option_id UUIDs must never surface as tenant-visible labels.
 */

import type { WorkspaceProviderOption } from '../../../api'
import {
  findProviderOptionById,
  isExplicitProviderSelectionInvalid,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  type ProviderSelectionMode,
} from './capabilityProviderSelection'

const PROVIDER_OPTION_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isProviderOptionUuid(value: string | null | undefined): boolean {
  const v = typeof value === 'string' ? value.trim() : ''
  return Boolean(v && PROVIDER_OPTION_UUID_RE.test(v))
}

/** True when a string looks like a raw id accidentally used as a label. */
export function isRawProviderUuidVisibleLabel(label: string | null | undefined): boolean {
  return isProviderOptionUuid(label)
}

export type CapabilityProviderSelectOption = {
  value: string
  label: string
  title?: string
  disabled?: boolean
}

export function resolveProviderOptionDisplayLabel(args: {
  providerOptionId: string
  catalogOption: WorkspaceProviderOption | null
  nodePersistedDisplayName: string | null | undefined
  workspaceCachedDisplayName: string | null | undefined
  catalogLoading: boolean
  loadingLabel: string
  unavailableSuffix: string
  catalogLoaded: boolean
  explicitSelectionInvalid: boolean
}): string {
  if (args.catalogOption) {
    const base = args.catalogOption.frontend_display_name || ''
    if (base && !isRawProviderUuidVisibleLabel(base)) {
      return isProviderOptionSelectable(args.catalogOption)
        ? base
        : `${base}${args.unavailableSuffix}`
    }
  }
  if (args.explicitSelectionInvalid && args.catalogLoaded) {
    const cached =
      sanitizeDisplayCandidate(args.nodePersistedDisplayName) ||
      sanitizeDisplayCandidate(args.workspaceCachedDisplayName)
    return cached ? `${cached}${args.unavailableSuffix}` : args.loadingLabel
  }
  const stale =
    sanitizeDisplayCandidate(args.nodePersistedDisplayName) ||
    sanitizeDisplayCandidate(args.workspaceCachedDisplayName)
  if (stale) return stale
  if (args.catalogLoading || !args.catalogLoaded) return args.loadingLabel
  return args.loadingLabel
}

function sanitizeDisplayCandidate(raw: string | null | undefined): string | null {
  const v = typeof raw === 'string' ? raw.trim() : ''
  if (!v || isRawProviderUuidVisibleLabel(v)) return null
  return v
}

export function buildCapabilityProviderSelectOptions(args: {
  catalogOptions: WorkspaceProviderOption[]
  resolvedProviderOptionId: string | null
  selectionMode: ProviderSelectionMode | string | null | undefined
  storedProviderOptionId: string | null | undefined
  nodePersistedDisplayName: string | null | undefined
  workspaceCachedDisplayName: string | null | undefined
  catalogLoading: boolean
  catalogError: boolean
  loadingLabel: string
  unavailableSuffix: string
  loadFailedPlaceholder: string
}): {
  options: CapabilityProviderSelectOption[]
  placeholder: string
  selectDisabled: boolean
  tooltipTitle: string
} {
  const mode = normalizeProviderSelectionMode(args.selectionMode)
  const catalogLoaded = !args.catalogLoading && args.catalogOptions.length > 0
  const explicitInvalid = isExplicitProviderSelectionInvalid({
    selectionMode: mode,
    storedProviderOptionId: args.storedProviderOptionId,
    options: args.catalogOptions,
  })

  const baseOptions: CapabilityProviderSelectOption[] = args.catalogOptions.map((opt) => {
    const available = isProviderOptionSelectable(opt)
    const base = opt.frontend_display_name || ''
    const safeBase = isRawProviderUuidVisibleLabel(base) ? args.loadingLabel : base || args.loadingLabel
    return {
      value: opt.provider_option_id,
      disabled: !available,
      label: available ? safeBase : `${safeBase}${args.unavailableSuffix}`,
      title: opt.frontend_description || safeBase,
    }
  })

  const resolvedId = typeof args.resolvedProviderOptionId === 'string' ? args.resolvedProviderOptionId.trim() : ''
  if (resolvedId && !baseOptions.some((o) => o.value === resolvedId)) {
    const catalogHit = findProviderOptionById(args.catalogOptions, resolvedId)
    const label = resolveProviderOptionDisplayLabel({
      providerOptionId: resolvedId,
      catalogOption: catalogHit,
      nodePersistedDisplayName:
        mode === 'EXPLICIT' && args.storedProviderOptionId === resolvedId ? args.nodePersistedDisplayName : null,
      workspaceCachedDisplayName: args.workspaceCachedDisplayName,
      catalogLoading: args.catalogLoading,
      loadingLabel: args.loadingLabel,
      unavailableSuffix: args.unavailableSuffix,
      catalogLoaded,
      explicitSelectionInvalid: explicitInvalid,
    })
    baseOptions.unshift({
      value: resolvedId,
      label,
      title: catalogHit?.frontend_description || label,
      disabled: args.catalogLoading || explicitInvalid || args.catalogError,
    })
  }

  const placeholder = args.catalogError ? args.loadFailedPlaceholder : args.loadingLabel
  const selectDisabled = args.catalogError && baseOptions.length === 0

  const resolvedOpt = resolvedId ? baseOptions.find((o) => o.value === resolvedId) : null
  const tooltipTitle = resolvedOpt?.title || resolvedOpt?.label || 'Provider'

  return {
    options: baseOptions,
    placeholder,
    selectDisabled,
    tooltipTitle,
  }
}
